import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

export function getAbsoluteApiUrl(path: string): string {
  if (path.startsWith('http')) return path
  const base = API_BASE_URL.startsWith('http') ? API_BASE_URL : window.location.origin + API_BASE_URL
  return `${base}${path.startsWith('/') ? path : `/${path}`}`
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// In-memory CSRF token fallback for the brief window before the non-httpOnly cookie is available.
let csrfToken: string | null = null
// In-memory impersonation token. Sent as a Bearer header only for the initial /auth/me call.
let impersonationToken: string | null = null

export function setCsrfToken(token: string | null) {
  csrfToken = token
}

export function setImpersonationToken(token: string | null) {
  impersonationToken = token
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

// Request interceptor: attach CSRF token for all cookie-authenticated requests.
// The access token is sent automatically as an httpOnly cookie via withCredentials.
// Backend CSRF protection is enforced on any request that carries the access_token cookie,
// so the header must be present for GETs (e.g. /auth/me) as well as mutating requests.
api.interceptors.request.use((config) => {
  if (impersonationToken) {
    config.headers.Authorization = `Bearer ${impersonationToken}`
  }

  const token = getCookie('csrf_token') || csrfToken
  if (token) {
    config.headers['X-CSRF-Token'] = token
  }

  return config
})

// Auth endpoints must surface 401s to the UI instead of redirecting
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/me',
  '/auth/refresh',
  '/auth/logout',
]

// Response interceptor: handle 401, refresh access token via HttpOnly cookie
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const url = originalRequest?.url || ''
    const detail = error.response?.data?.detail

    if (error.response?.status === 402 && detail?.error === 'SUBSCRIPTION_REQUIRED') {
      window.dispatchEvent(new CustomEvent('subscription-required', {
        detail: {
          message: detail.message,
          actionUrl: detail.action_url || '/billing',
        },
      }))
    }

    if (
      error.response?.status === 403 &&
      detail?.error === 'ACCOUNT_CLOSING_READ_ONLY' &&
      window.location.pathname !== '/account-closure'
    ) {
      window.location.href = detail.action_url || '/account-closure'
      return Promise.reject(error)
    }

    // Never redirect or intercept 401s from auth endpoints — let the UI handle them
    if (AUTH_ENDPOINTS.some((ep) => url.includes(ep))) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        // The refresh token is stored in an HttpOnly cookie; withCredentials sends it automatically.
        const res = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true })
        const { csrf_token } = res.data
        if (csrf_token) {
          setCsrfToken(csrf_token)
        }
        return api(originalRequest)
      } catch {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)
